import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TopPerformersData } from './gamificationTypes';

// Top performers — gamification concept L4, Cian feedback v2: two tabs
// (This Week / All Time), both ranked by lessons completed, and the
// motivational copy states exactly how many lessons break into the Top 3.
// Shape unchanged from the evidence: top 3 + you, never the full ranking,
// never who is below you (JWL 2025 — leaderboards demotivate the bottom).
// Open decisions: week start day, timezone and tie-break rule are TBD.

const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const TEXT_TERTIARY = '#868686';
const BRAND_LIGHT = '#F79420';

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  py: '5px'
} as const;

type TabKey = 'thisWeek' | 'allTime';

interface Props {
  /** Live boards from the summary endpoint — consumers only render the
      card once these exist (no placeholder names, ever). */
  data: TopPerformersData;
  /** Admin view has no personal position to show — hides the "You" row and
      the lessons-to-Top-3 line. */
  showYou?: boolean;
}

const TopPerformersCard: React.FC<Props> = ({ data, showYou = true }) => {
  const [tab, setTab] = useState<TabKey>('thisWeek');
  const { top, you, lessonsToTop3 } = data[tab];

  return (
    <Box
      sx={{
        // Sizes to its content (the Top-3 line is nowrap) rather than being
        // squeezed to a fixed basis.
        flex: '1 1 auto',
        minWidth: 280,
        bgcolor: SURFACE,
        border: `1px solid ${BORDER_DIM}`,
        borderRadius: '8px',
        p: '14px 20px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
        <Typography
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            color: BRAND_LIGHT,
            lineHeight: 1,
            whiteSpace: 'nowrap'
          }}
        >
          {/* Same icon as the chip that opens this card. */}
          <Box component="span" aria-hidden sx={{ fontSize: 13, letterSpacing: 0 }}>
            🏅
          </Box>
          TOP PERFORMERS
        </Typography>
        {(
          [
            { key: 'thisWeek', label: 'This Week' },
            { key: 'allTime', label: 'All Time' }
          ] as Array<{ key: TabKey; label: string }>
        ).map(({ key, label }) => (
          <Box
            key={key}
            onClick={() => setTab(key)}
            sx={{
              px: '10px',
              py: '4px',
              borderRadius: '999px',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              fontSize: 11.5,
              fontWeight: 600,
              color: tab === key ? TEXT_PRIMARY : TEXT_TERTIARY,
              border: `1px solid ${tab === key ? '#565656' : BORDER_DIM}`,
              bgcolor: tab === key ? '#252525' : 'transparent',
              transition: 'color 0.15s ease, border-color 0.15s ease'
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {top.map((p) => (
        <Box key={p.rank} sx={rowSx}>
          <Typography sx={{ width: 16, fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_TERTIARY }}>
            {p.rank}
          </Typography>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              color: TEXT_PRIMARY,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {p.name}
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_SECONDARY }}>
            {p.lessons} lessons
          </Typography>
        </Box>
      ))}

      {/* Your position — no one below you is ever shown. */}
      {showYou && (
        <>
          <Box sx={{ ...rowSx, borderTop: `1px solid ${BORDER_DIM}`, mt: '4px', pt: '8px' }}>
            <Typography sx={{ width: 16, fontFamily: 'Inter, sans-serif', fontSize: 13, color: BRAND_LIGHT }}>
              {you.rank}
            </Typography>
            <Typography
              sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: BRAND_LIGHT }}
            >
              You
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_SECONDARY }}>
              {you.lessons} lessons
            </Typography>
          </Box>
          {/* One line always — the card widens to fit rather than wrapping this
              when All Time shows a bigger gap to the Top 3 than This Week. */}
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              color: TEXT_TERTIARY,
              mt: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            Complete {lessonsToTop3} more {lessonsToTop3 === 1 ? 'lesson' : 'lessons'} to break into the current Top 3.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default TopPerformersCard;
